/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'

/**
 * Import async components.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'breadcrumbs': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.breadcrumbs' */'~async/breadcrumbs/breadcrumbs') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'collection-links': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-links' */'~async/collection-links/collection-links') }),
    'contact-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.contact-page' */'~async/contact-page/contact-page') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers/account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers/activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers/addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers/login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers/order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers/orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers/register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers/reset-password/customers-reset-password') }),
    'product-discovery': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.discovery' */'~async/discovery/product-discovery') }),
    'dual-image-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.dual-image-banner' */'~async/dual-image-banner/dual-image-banner') }),
    'editorial-gallery': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.editorial-gallery' */'~async/editorial-gallery/editorial-gallery') }),
    'faq-main': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.faq-main' */'~async/faq-main/faq-main') }),
    'featured-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.featured-collections' */'~async/featured-collections/featured-collections') }),
    'header-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.header-search' */'~async/header-search/header-search') }),
    'header-search-mobile': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.header-search-mobile' */'~async/header-search-mobile/header-search-mobile') }),
    'hero-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.hero-section' */'~async/hero-section/hero-section') }),
    'html-content': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.html-content' */'~async/html-content/html-content') }),
    'image-cards': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.image-cards' */'~async/image-cards/image-cards') }),
    'image-gallery': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.image-gallery' */'~async/image-gallery/image-gallery') }),
    'main-404': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-404' */'~async/main-404/main-404') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-glossary': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-glossary' */'~async/main-glossary/main-glossary') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-page' */'~async/main-page/main-page') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'measurement-toggle': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.measurement-toggle' */'~async/measurement-toggle/measurement-toggle') }),
    'product-add-on-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-add-on-drawer' */'~async/product-add-on-drawer/product-add-on-drawer') }),
    'product-badges': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-badges' */'~async/product-badges/product-badges') }),
    'product-comparison': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-comparison' */'~async/product-comparison/product-comparison') }),
    'product-details': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-details' */'~async/product-details/product-details') }),
    'product-recommendations': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-recommendations' */'~async/product-recommendations/product-recommendations') }),
    'promo-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.promo-banner' */'~async/promo-banner/promo-banner') }),
    'quote-block': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.quote-block' */'~async/quote-block/quote-block') }),
    'search-bar': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.search-bar' */'~async/search-bar/search-bar') }),
    'side-by-side-content': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.side-by-side-content' */'~async/side-by-side-content/side-by-side-content') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer-group/site-footer/site-footer') }),
    'ticker-tape': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.ticker-tape' */'~async/ticker-tape/ticker-tape') }),
    'transition-height': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.transition-height' */'~async/transition-height/transition-height') }),
    'usps-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.usps-banner' */'~async/usps-banner/usps-banner') }),
    'video-player-responsive': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.video-player-responsive' */'~async/video-player-responsive/video-player-responsive') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import Index from '~global/core/index'
import MegaNav from '~global/mega-nav/mega-nav'
import MultiStoreForm from '~global/multi-store-form/multi-store-form'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~global/product-card/product-card'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SiteHeader from '~global/site-header-group/site-header/site-header'
import SiteHeaderGroup from '~global/site-header-group/site-header-group'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'btn': Btn,
    'index': Index,
    'mega-nav': MegaNav,
    'multi-store-form': MultiStoreForm,
    'overlay': Overlay,
    'product-card': ProductCard,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'site-header': SiteHeader,
    'site-header-group': SiteHeaderGroup,
    // canvas-global-object-end
  }
}

/**
 * Import web components.
 * - Must be manually updated with new async web components (components/async).
 * - Use component command import to update.
 */
// canvas-web-import-start
import(/* webpackChunkName: 'web.faq-landing' */'~async/faq-landing/faq-landing')
import(/* webpackChunkName: 'web.faq-section' */'~async/faq-section/faq-section')
import(/* webpackChunkName: 'web.tooltip-popup' */'~async/tooltip-popup/tooltip-popup')
import(/* webpackChunkName: 'web.web-video-player' */'~async/web-video-player/web-video-player')
// canvas-web-import-end

/**
 * Import Vuex stores.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import OverlaysStore from '~stores/overlays'
import AddOnStore from '~stores/add-on'

// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'overlays': OverlaysStore,
    'add-on': AddOnStore,
    // canvas-stores-object-end
  }
}
